import { Button, CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { DATE_TIME_FORMAT } from '../../../constants';
import { applyServerTimezone } from '../../../helpers';
import { useRecordingsSearchContext } from '../../../hooks';
import {
  ListenRecordingModal,
  ReadCallNotesModal,
  StoplightAnalysisModal,
} from '../../Modals';
import { IconButton } from '../../Shared';
import { Item } from './Item';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_LOADING: 'isLoading',
  IS_ERROR: 'isError',
};

const getState = (data, isLoading, isError, isSearchByContactNameValid) => {
  if (isLoading) return STATE.IS_LOADING;
  if (isError) return STATE.IS_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData || !isSearchByContactNameValid) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

const getFormattedRecordDate = (date, format) =>
  moment(applyServerTimezone(date)).format(format);

export const Playback = ({ backAction }) => {
  const { requestBody, isSearchByContactNameValid } =
    useRecordingsSearchContext();
  const [callRecordings, setCallRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchCallRecordings = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    if (!isSearchByContactNameValid) {
      setCallRecordings([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    try {
      const response = await fetch('/call-recordings', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && {
          body: JSON.stringify(requestBody),
        }),
      });

      if (response.status !== 200) throw new Error();

      const data = await response.json();
      setCallRecordings(data.callRecordings?.query1 ?? []);
      setIsLoading(false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsLoading(false);
      setIsError(true);
      toast.error('Failed to load call recordings!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [requestBody, isSearchByContactNameValid]);

  useEffect(() => {
    fetchCallRecordings();
  }, [fetchCallRecordings]);

  const [currentRec, setCurrentRec] = useState({});
  const [isListenRecordingModalOpened, setIsListenRecordingModalOpened] =
    useState(false);
  const [isReadCallNotesModalOpened, setIsReadCallNotesModalOpened] =
    useState(false);
  const [isStoplightAnalysisModalOpened, setIsStoplightAnalysisModalOpened] =
    useState(false);

  const openListenRecordingModal = () => setIsListenRecordingModalOpened(true);
  const closeListenRecordingModal = () =>
    setIsListenRecordingModalOpened(false);
  const openReadCallNotesModal = () => setIsReadCallNotesModalOpened(true);
  const closeReadCallNotesModal = () => setIsReadCallNotesModalOpened(false);
  const openStoplightAnalysisModal = () =>
    setIsStoplightAnalysisModalOpened(true);
  const closeStoplightAnalysisModal = () =>
    setIsStoplightAnalysisModalOpened(false);

  const [hmmNumber, setHmmNumber] = useState(0);
  const [confirmedContact, setConfirmedContact] = useState(false);
  const [callLength, setCallLength] = useState(0);
  const [callerTalkTime, setCallerTalkTime] = useState(0);
  const [followedScript, setFollowedScript] = useState(0);
  const [analysisScore, setAnalysisScore] = useState(0);

  const initStoplightValues = () => {
    setFollowedScript(0);
    setCallerTalkTime(0);
    setHmmNumber(0);
    setConfirmedContact(0);
    setAnalysisScore(0);
  };

  function checkForConfirmationRequest(callerTranscripts) {
    const wholeText = callerTranscripts.map(item => item.text).join(' ');
    const emailPattern1 = /Can you confirm your email (address)?/i;
    const emailPattern2 = /I have your email as/i;
    const emailPattern3 = /can I confirm that your email/i;
    const phonePattern1 = /Can you confirm your phone (number)?/i;
    const phonePattern2 = /What is the (best)? phone (number)? to reach you/i;

    const asksForPhoneConfirmation =
      phonePattern1.test(wholeText) || phonePattern2.test(wholeText);
    const asksForEmailConfirmation =
      emailPattern1.test(wholeText) ||
      emailPattern2.test(wholeText) ||
      emailPattern3.test(wholeText);

    return asksForPhoneConfirmation || asksForEmailConfirmation;
  }

  function countFillerWords(callerTranscripts) {
    const wholeText = callerTranscripts.map(item => item.text).join(' ');
    const cleanedText = wholeText.replace(/[^\w\s]/g, '').toLowerCase();
    const fillerWords = ['hmm', 'umm', 'uh', 'ah', 'um', 'uhm', 'mhm'];
    const pattern = new RegExp(`\\b(${fillerWords.join('|')})\\b`, 'gi');
    const matches = wholeText.match(pattern);
    const count = matches ? matches.length : 0;

    return count;
  }

  function calcFollowedScript(transcriptCL) {
    const calllerTranscripts = transcriptCL
      .filter(obj => obj.channel === '1')
      .map(item => item.text)
      .join(' ');
    const wholeTranscripts = transcriptCL.map(item => item.text).join(' ');
    const callerText = calllerTranscripts.replace(/[^\w\s]/g, '').toLowerCase();
    const wholeText = wholeTranscripts.replace(/[^\w\s]/g, '').toLowerCase();
    const callerWords = callerText.split(/\s+/).filter(Boolean);
    const allWords = wholeText.split(/\s+/).filter(Boolean);
    const totalWordsInCaller = callerWords.length;
    const totalWordsInText = allWords.length;
    const percentage = ((totalWordsInCaller / totalWordsInText) * 100).toFixed(
      0,
    );

    return percentage;
  }

  function calculateCallerTalkTime(transcriptCL) {
    const callerTimeArr = transcriptCL
      .filter(obj => obj.channel === '1')
      .map(item => item.end - item.start);
    const wholeTimeArr = transcriptCL.map(item => item.end - item.start);
    const callerTime = callerTimeArr.reduce((acc, curr) => acc + curr, 0);
    const wholeTime = wholeTimeArr.reduce((acc, curr) => acc + curr, 0);
    const percentage = ((callerTime / wholeTime) * 100).toFixed(0);

    return percentage;
  }

  useEffect(() => {
    if (currentRec.CLTranscript) {
      const transcriptCL = JSON.parse(currentRec.CLTranscript);
      if (transcriptCL) {
        const calllerTranscript = transcriptCL.filter(
          obj => obj.channel === '1',
        );
        if (calllerTranscript.length) {
          const followedScriptPercent = calcFollowedScript(transcriptCL);
          const callerTimePercent = calculateCallerTalkTime(transcriptCL);
          const hmmCount = countFillerWords(calllerTranscript);
          const contactConfirmStatus =
            checkForConfirmationRequest(calllerTranscript);
          setCallerTalkTime(callerTimePercent);
          setFollowedScript(followedScriptPercent);
          setHmmNumber(hmmCount);
          setConfirmedContact(contactConfirmStatus);
          const followedScriptScore = Math.max(
            0,
            45 - 0.5 * (100 - followedScriptPercent),
          );
          var callerTimeScore = 30;
          if (callerTimePercent > 60) {
            callerTimeScore -= 0.5 * (callerTimePercent - 60);
          } else if (callerTimePercent < 40) {
            callerTimeScore -= 0.5 * (40 - callerTimePercent);
          }
          const callLengthScore = Math.max(
            0,
            20 - (5 * (300 - callLength)) / 60,
          );
          const confirmScore = 5;
          const totalScore =
            followedScriptScore +
            callerTimeScore +
            callLengthScore +
            confirmScore -
            hmmNumber;
          setAnalysisScore(totalScore);
        } else {
          initStoplightValues();
        }
      }
    } else {
      initStoplightValues();
    }

    const audio = new Audio(currentRec.RecordingURL);
    const handleMetadata = () => {
      setCallLength(audio.duration);
    };
    audio.addEventListener('loadedmetadata', handleMetadata);

    return () => {
      audio.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [currentRec, callLength]);

  const state = getState(
    callRecordings,
    isLoading,
    isError,
    isSearchByContactNameValid,
  );

  return (
    <div className="flex flex-col gap-3 flex-1">
      <div className="flex items-center gap-2">
        <IconButton
          buttonProps={{
            className: 'w-8 h-8',
            onClick: backAction,
          }}
          iconProps={{
            name: 'arrow-left',
            className: 'h-5',
          }}
        />
        <span className="text-base-1000 font-semibold text-[18px]/[24px]">
          Playback
        </span>
      </div>
      <div className="flex flex-col gap-3 rounded-xl round items-center max-h-[380px] h-max-h-[480px] overflow-auto playback-scrollbar pr-2 h-full">
        {state === STATE.IS_LOADING && (
          <div className="flex justify-center items-center h-full">
            <CircularProgress size={80} sx={{ color: '#1F68A2' }} />
          </div>
        )}
        {state === STATE.IS_ERROR && (
          <div className="flex justify-center items-center h-full flex-col gap-6">
            <div className="flex justify-center items-center flex-col gap-3">
              <span className="text-2xl font-medium">
                Failed to load call recordings
              </span>
              <span className="text-lg font-medium text-[#6b7280]">
                Error occurred while loading. Try again.
              </span>
            </div>
            <Button
              disableFocusRipple
              disableRipple
              variant="text"
              sx={{
                color: '#FF344C',
                fontFamily: 'inherit',
                textTransform: 'none',
                fontSize: 21,
                padding: 0,
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: 'transparent',
                  color: '#FF5A6D',
                },
              }}
              onClick={fetchCallRecordings}
            >
              Reload
            </Button>
          </div>
        )}
        {state === STATE.HAS_NO_DATA && (
          <div className="flex justify-center items-center h-full">
            <p>There are no records to display</p>
          </div>
        )}
        {state === STATE.HAS_DATA &&
          callRecordings.map((item, index) => (
            <Item
              key={index}
              item={item}
              setCurrentRec={setCurrentRec}
              openListenRecordingModal={openListenRecordingModal}
              getFormattedRecordDate={getFormattedRecordDate}
              openReadCallNotesModal={openReadCallNotesModal}
              openStoplightAnalysisModal={openStoplightAnalysisModal}
            />
          ))}
      </div>
      {isListenRecordingModalOpened && (
        <ListenRecordingModal
          open={isListenRecordingModalOpened}
          onClose={closeListenRecordingModal}
          recording={{
            contactName: currentRec.ContactName,
            date: getFormattedRecordDate(
              currentRec.Timestamp,
              DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A,
            ),
            recordingURL: currentRec.RecordingURL,
          }}
        />
      )}
      {isReadCallNotesModalOpened && (
        <ReadCallNotesModal
          open={isReadCallNotesModalOpened}
          onClose={closeReadCallNotesModal}
          recording={{
            tenant: currentRec.Tenant,
            callerName: currentRec.CallerName,
            contactName: currentRec.ContactName,
            companyName: currentRec.CompanyName,
            date: getFormattedRecordDate(
              currentRec.Timestamp,
              DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A,
            ),
            callDuration: currentRec.CallDuration,
            callOutcome: currentRec.CallType,
            clTranscript: currentRec.CLTranscript,
          }}
        />
      )}
      {isStoplightAnalysisModalOpened && (
        <StoplightAnalysisModal
          open={isStoplightAnalysisModalOpened}
          onClose={closeStoplightAnalysisModal}
          selectedRecording={currentRec}
          getFormattedRecordDate={getFormattedRecordDate}
          callLength={callLength}
          hmmNumber={hmmNumber}
          callerTalkTime={callerTalkTime}
          followedScript={followedScript}
          analysisScore={analysisScore}
        />
      )}
    </div>
  );
};
